/**
 * PaneGrid — the d&d canvas, @dnd-kit edition.
 *
 * Renders tiles into a 12-column CSS grid. Drag from the tile
 * header moves; drag from the bottom-right corner resizes. Both
 * snap to grid cells. After every committed change the layout is
 * gravity-compacted so tiles slide up to fill gaps.
 *
 * Why @dnd-kit and not raw pointer events:
 *
 *   • Accessibility for free — keyboard nav (arrow keys move, +/-
 *     resize), screen-reader announcements, focus management.
 *   • Touch + mouse from one sensor; no manual pointermove plumbing.
 *   • A composable drag overlay that survives parent re-renders
 *     and stays smooth even when state churns.
 *
 * Snap behaviour lives in modifiers + the onDragEnd handler. The
 * collision-resolve + gravity-up algorithm is unchanged from the
 * pointer-only edition — the math has no opinion about the input.
 *
 * Spec: Docs/architecture/24-omni-pane.md §6 ("Grid engine").
 */
import * as React from "react";
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useDraggable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
  type Modifier,
} from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import {
  ChevronDown,
  ChevronRight,
  GripVertical,
  Maximize2,
  Pencil,
  Pin,
  PinOff,
  RefreshCw,
  X,
} from "lucide-react";
import { cn } from "@/lib/cn";
import { resolveTile } from "./registry";
import type { PaneTile } from "./types";
import { PANE_COLS } from "./types";

const ROW_HEIGHT = 56;
const GAP_PX = 10;

interface PaneGridProps {
  tiles: PaneTile[];
  onMove: (id: string, geom: { x: number; y: number }) => void;
  onResize: (id: string, geom: { w: number; h: number }) => void;
  onRemove: (id: string) => void;
  onExpand: (id: string) => void;
  /** Title, data, pin or collapse — the tile's OCC PATCH. */
  onUpdate: (id: string, patch: TilePatch) => void;
  /** Ask the agent to bring the tile up to date (its `data.refresh`). */
  onRefresh: (tile: PaneTile) => void;
  newTileIds?: ReadonlySet<string>;
  voiceTargetId?: string | null;
}

export interface TilePatch {
  title?: string;
  data?: Record<string, unknown>;
  pinned?: boolean;
  collapsed?: boolean;
}

// ─── geometry helpers ───────────────────────────────────────────────────

/**
 * Rows a tile takes on the grid: a collapsed tile is its header only.
 * The stored `h` is kept, so it opens at its old height.
 */
export function rowsOf(t: { h: number; collapsed?: boolean }): number {
  return t.collapsed ? 1 : t.h;
}

/**
 * AABB overlap check on grid-cell rectangles. Exported because the
 * Phase B "agent pushes a tile" path uses it to find the first
 * empty slot for new tiles.
 */
export function intersects(
  a: { x: number; y: number; w: number; h: number },
  b: { x: number; y: number; w: number; h: number },
): boolean {
  return !(
    a.x + a.w <= b.x ||
    b.x + b.w <= a.x ||
    a.y + a.h <= b.y ||
    b.y + b.h <= a.y
  );
}

/**
 * Gravity-up compaction: every tile slides up until it lands on
 * another tile or the top edge. Used to fill gaps left by resize
 * or remove operations. Pure function — safe to call in render.
 */
export function compactUp(tiles: PaneTile[]): PaneTile[] {
  const sorted = [...tiles].sort((a, b) => a.y - b.y || a.x - b.x);
  const placed: PaneTile[] = [];
  for (const t of sorted) {
    let ny = 0;
    for (const p of placed) {
      const xOverlap = !(p.x + p.w <= t.x || t.x + t.w <= p.x);
      if (xOverlap) ny = Math.max(ny, p.y + rowsOf(p));
    }
    placed.push({ ...t, y: ny });
  }
  return placed;
}

// ─── grid metrics ───────────────────────────────────────────────────────

function useGridMetrics(boardRef: React.RefObject<HTMLDivElement>) {
  const [metrics, setMetrics] = React.useState({
    colW: 80,
    rowH: ROW_HEIGHT,
    gap: GAP_PX,
  });
  React.useEffect(() => {
    if (!boardRef.current) return;
    const measure = () => {
      const grid = boardRef.current?.querySelector(
        ".pane-grid-inner",
      ) as HTMLElement | null;
      if (!grid) return;
      const style = getComputedStyle(grid);
      const gap = parseFloat(style.columnGap) || GAP_PX;
      const totalW = grid.clientWidth;
      const colW = (totalW - gap * (PANE_COLS - 1)) / PANE_COLS;
      setMetrics({ colW, rowH: ROW_HEIGHT, gap });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(boardRef.current);
    return () => ro.disconnect();
  }, [boardRef]);
  return metrics;
}

// ─── snap modifier ──────────────────────────────────────────────────────
// Snaps the drag transform to grid cells so the moving tile + drop
// ghost stick to cells instead of floating between them.

function snapModifier(colW: number, rowH: number, gap: number): Modifier {
  const cellW = colW + gap;
  const cellH = rowH + gap;
  return ({ transform }) => ({
    ...transform,
    x: Math.round(transform.x / cellW) * cellW,
    y: Math.round(transform.y / cellH) * cellH,
  });
}

// ─── component ──────────────────────────────────────────────────────────

interface DragState {
  id: string;
  mode: "move" | "resize";
  start: { x: number; y: number; w: number; h: number };
}

export function PaneGrid({
  tiles,
  onMove,
  onResize,
  onRemove,
  onExpand,
  onUpdate,
  onRefresh,
  newTileIds,
  voiceTargetId,
}: PaneGridProps) {
  const boardRef = React.useRef<HTMLDivElement>(null);
  const { colW, rowH, gap } = useGridMetrics(boardRef);
  const [drag, setDrag] = React.useState<DragState | null>(null);

  // Activation distance keeps incidental clicks from starting drags
  // (the FAB and chrome buttons stay clickable).
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor),
  );

  const handleStart = (e: DragStartEvent) => {
    const dragId = String(e.active.id);
    // active.id is "<tileId>:move" or "<tileId>:resize"
    const [tileId, mode] = dragId.split(":") as [string, "move" | "resize"];
    const tile = tiles.find((t) => t.id === tileId);
    if (!tile) return;
    setDrag({
      id: tileId,
      mode,
      start: { x: tile.x, y: tile.y, w: tile.w, h: rowsOf(tile) },
    });
  };

  const handleEnd = (e: DragEndEvent) => {
    const d = drag;
    setDrag(null);
    if (!d) return;
    const cellW = colW + gap;
    const cellH = rowH + gap;
    const dxCell = Math.round(e.delta.x / cellW);
    const dyCell = Math.round(e.delta.y / cellH);

    if (d.mode === "move") {
      const nx = Math.max(0, Math.min(PANE_COLS - d.start.w, d.start.x + dxCell));
      const ny = Math.max(0, d.start.y + dyCell);
      if (nx !== d.start.x || ny !== d.start.y) {
        onMove(d.id, { x: nx, y: ny });
      }
    } else {
      const nw = Math.max(
        1,
        Math.min(PANE_COLS - d.start.x, d.start.w + dxCell),
      );
      const nh = Math.max(2, d.start.h + dyCell);
      if (nw !== d.start.w || nh !== d.start.h) {
        onResize(d.id, { w: nw, h: nh });
      }
    }
  };

  // Live "ghost target" rectangle computed from drag delta. Rendered
  // as a dashed cell-shaped placeholder so users see where the tile
  // will land.
  const [liveDelta, setLiveDelta] = React.useState<{ dx: number; dy: number }>({
    dx: 0,
    dy: 0,
  });
  const handleMove = (e: { delta: { x: number; y: number } }) => {
    setLiveDelta({ dx: e.delta.x, dy: e.delta.y });
  };

  const ghost = React.useMemo(() => {
    if (!drag) return null;
    const cellW = colW + gap;
    const cellH = rowH + gap;
    const dxCell = Math.round(liveDelta.dx / cellW);
    const dyCell = Math.round(liveDelta.dy / cellH);
    if (drag.mode === "move") {
      return {
        x: Math.max(0, Math.min(PANE_COLS - drag.start.w, drag.start.x + dxCell)),
        y: Math.max(0, drag.start.y + dyCell),
        w: drag.start.w,
        h: drag.start.h,
      };
    }
    return {
      x: drag.start.x,
      y: drag.start.y,
      w: Math.max(1, Math.min(PANE_COLS - drag.start.x, drag.start.w + dxCell)),
      h: Math.max(2, drag.start.h + dyCell),
    };
  }, [drag, liveDelta, colW, rowH, gap]);

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleStart}
      onDragMove={handleMove}
      onDragEnd={handleEnd}
      onDragCancel={() => setDrag(null)}
      modifiers={[snapModifier(colW, rowH, gap)]}
    >
      <div
        ref={boardRef}
        className={cn(
          "h-full w-full overflow-auto p-3",
          drag && "[&_*]:select-none",
        )}
        style={{
          backgroundImage:
            "radial-gradient(circle at 1px 1px, var(--om-color-border) 1px, transparent 1px)",
          backgroundSize: "16px 16px",
        }}
      >
        <div
          className="pane-grid-inner relative grid"
          style={{
            gridTemplateColumns: `repeat(${PANE_COLS}, minmax(0, 1fr))`,
            gridAutoRows: `${ROW_HEIGHT}px`,
            gap: `${GAP_PX}px`,
            minHeight: "100%",
          }}
        >
          {ghost && (
            <div
              className="border-2 border-dashed border-accent/80 bg-accent/10"
              style={{
                gridColumn: `${ghost.x + 1} / span ${ghost.w}`,
                gridRow: `${ghost.y + 1} / span ${ghost.h}`,
                pointerEvents: "none",
              }}
            />
          )}
          {tiles.map((t) => (
            <DraggableTile
              key={t.id}
              tile={t}
              isDragging={drag?.id === t.id}
              isNew={newTileIds?.has(t.id) ?? false}
              isVoiceTarget={voiceTargetId === t.id}
              onRemove={() => onRemove(t.id)}
              onExpand={() => onExpand(t.id)}
              onUpdate={(patch) => onUpdate(t.id, patch)}
              onRefresh={() => onRefresh(t)}
            />
          ))}
        </div>
      </div>

      {/* DragOverlay renders the moving tile in a portal so its
          transform stays smooth even when the underlying grid re-
          renders. Only used for the "move" mode; resize is in-place
          because growing one tile shouldn't pull it out of layout. */}
      <DragOverlay dropAnimation={null}>
        {drag?.mode === "move" ? (
          <OverlayTile
            tile={tiles.find((t) => t.id === drag.id)!}
            colW={colW}
            rowH={rowH}
            gap={gap}
          />
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}

// ─── individual draggable tile ──────────────────────────────────────────

function DraggableTile({
  tile,
  isDragging,
  isNew,
  isVoiceTarget,
  onRemove,
  onExpand,
  onUpdate,
  onRefresh,
}: {
  tile: PaneTile;
  isDragging: boolean;
  isNew: boolean;
  isVoiceTarget: boolean;
  onRemove: () => void;
  onExpand: () => void;
  onUpdate: (patch: TilePatch) => void;
  onRefresh: () => void;
}) {
  const [editing, setEditing] = React.useState(false);
  const moveDraggable = useDraggable({
    id: `${tile.id}:move`,
    data: { tileId: tile.id, mode: "move" },
  });
  const resizeDraggable = useDraggable({
    id: `${tile.id}:resize`,
    data: { tileId: tile.id, mode: "resize" },
  });

  const def = resolveTile(tile.kind);
  const Icon = def.Icon;
  const Body = def.Body;
  const Editor = def.Editor;
  const collapsed = Boolean(tile.collapsed);
  const refresh = typeof tile.data.refresh === "string" ? tile.data.refresh : null;

  return (
    <div
      ref={moveDraggable.setNodeRef}
      className={cn(
        // Square corners — design system hard rule (border-radius: 0
        // everywhere). No bespoke radius var.
        "group relative flex flex-col overflow-hidden border bg-bg-elevated",
        "border-border transition-shadow",
        isDragging && "z-20 opacity-50",
        isNew && "animate-pane-tile-in",
        isVoiceTarget && "ring-2 ring-accent ring-offset-0 ring-offset-bg",
      )}
      style={{
        gridColumn: `${tile.x + 1} / span ${tile.w}`,
        gridRow: `${tile.y + 1} / span ${rowsOf(tile)}`,
      }}
      data-tile-id={tile.id}
      data-collapsed={collapsed ? "true" : "false"}
    >
      <header
        ref={moveDraggable.setActivatorNodeRef}
        {...moveDraggable.listeners}
        {...moveDraggable.attributes}
        className={cn(
          "flex flex-shrink-0 cursor-grab select-none items-center gap-2 bg-bg-sunken/60 px-2 py-1.5",
          !collapsed && "border-b border-border",
          isDragging && "cursor-grabbing",
        )}
      >
        <IconBtn
          label={collapsed ? "Open tile" : "Fold tile"}
          onClick={() => onUpdate({ collapsed: !collapsed })}
        >
          {collapsed ? <ChevronRight size={11} /> : <ChevronDown size={11} />}
        </IconBtn>
        <span className="flex h-5 w-5 items-center justify-center bg-accent/15 text-accent">
          <Icon size={12} />
        </span>
        {/* Title sized to its text and the last to give way; the source
            label shrinks first. ``flex-1`` (basis 0) left the title only
            leftover space, so at default widths it showed as nothing. */}
        <span className="min-w-0 flex-auto truncate text-[12px] font-semibold tracking-tight text-fg">
          {tile.title || def.title}
        </span>
        {tile.source && (
          <span className="hidden min-w-0 shrink-[4] truncate font-mono text-[10px] text-fg-faint sm:inline">
            · {tile.source}
          </span>
        )}
        {tile.pinned && (
          <span className="text-accent" title="Pinned — Clear keeps it">
            <Pin size={11} />
          </span>
        )}
        <div className="flex items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100">
          {refresh && (
            <IconBtn label="Refresh" onClick={onRefresh}>
              <RefreshCw size={11} />
            </IconBtn>
          )}
          <IconBtn
            label={tile.pinned ? "Unpin" : "Pin"}
            onClick={() => onUpdate({ pinned: !tile.pinned })}
          >
            {tile.pinned ? <PinOff size={11} /> : <Pin size={11} />}
          </IconBtn>
          {Editor && !editing && !collapsed && (
            <IconBtn label="Edit" onClick={() => setEditing(true)}>
              <Pencil size={11} />
            </IconBtn>
          )}
          <IconBtn label="Expand" onClick={onExpand}>
            <Maximize2 size={11} />
          </IconBtn>
          <IconBtn label="Remove" onClick={onRemove} danger>
            <X size={11} />
          </IconBtn>
        </div>
        <span className="ml-0.5 text-fg-faint">
          <GripVertical size={11} />
        </span>
      </header>

      {!collapsed && (
        <div
          className={cn(
            "min-h-0 flex-1 overflow-auto",
            editing || !def.flush ? "p-3" : "p-0",
          )}
        >
          {Editor && editing ? (
            <Editor
              data={tile.data}
              title={tile.title}
              onSave={(patch) => {
                onUpdate(patch);
                setEditing(false);
              }}
              onCancel={() => setEditing(false)}
            />
          ) : (
            <Body
              data={tile.data}
              w={tile.w}
              h={tile.h}
              onChange={(data) => onUpdate({ data })}
            />
          )}
        </div>
      )}

      {/* Bottom-right resize handle — its own draggable so the
          move-vs-resize intent is signalled by which element the
          user pressed on. None while folded: there is no body to size. */}
      <span
        hidden={collapsed}
        ref={resizeDraggable.setNodeRef}
        {...resizeDraggable.listeners}
        {...resizeDraggable.attributes}
        className="absolute bottom-0 right-0 z-10 h-4 w-4 cursor-nwse-resize touch-none"
        title="Resize tile"
        aria-label="Resize tile"
      >
        <span
          className="absolute bottom-1 right-1 h-2 w-2 bg-fg-faint/60 transition group-hover:bg-accent"
          style={{
            clipPath:
              "polygon(100% 0, 100% 100%, 0 100%, 50% 50%, 0 50%, 50% 50%, 0 0)",
          }}
        />
      </span>
    </div>
  );
}

// ─── overlay (move-only portal) ─────────────────────────────────────────

function OverlayTile({
  tile,
  colW,
  rowH,
  gap,
}: {
  tile: PaneTile;
  colW: number;
  rowH: number;
  gap: number;
}) {
  const def = resolveTile(tile.kind);
  const Icon = def.Icon;
  const widthPx = tile.w * colW + (tile.w - 1) * gap;
  const rows = rowsOf(tile);
  const heightPx = rows * rowH + (rows - 1) * gap;
  return (
    <div
      className={cn(
        "flex flex-col overflow-hidden border-2 border-accent bg-bg-elevated shadow-2xl",
      )}
      style={{
        width: `${widthPx}px`,
        height: `${heightPx}px`,
        transform: `${CSS.Translate.toString({ x: 0, y: 0, scaleX: 1, scaleY: 1 })}`,
      }}
    >
      <header className="flex items-center gap-2 border-b border-border bg-bg-sunken/80 px-2 py-1.5">
        <span className="flex h-5 w-5 items-center justify-center bg-accent/15 text-accent">
          <Icon size={12} />
        </span>
        <span className="flex-1 truncate text-[12px] font-semibold tracking-tight text-fg">
          {tile.title || def.title}
        </span>
      </header>
      <div className="min-h-0 flex-1 overflow-hidden p-3 text-fg opacity-60" aria-hidden>
        {/* Skeleton — overlay is purely positional; the real body
            stays in its original cell to keep paint cheap. */}
      </div>
    </div>
  );
}

function IconBtn({
  children,
  label,
  onClick,
  danger,
}: {
  children: React.ReactNode;
  label: string;
  onClick: () => void;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className={cn(
        "flex h-5 w-5 items-center justify-center text-fg-faint transition",
        danger ? "hover:bg-danger/15 hover:text-danger" : "hover:bg-bg-sunken hover:text-fg",
      )}
    >
      {children}
    </button>
  );
}
